import type { AliareFieldMapping, OrgSetupCustomField } from '@src/services/org-config/dto';

export interface ExcelMappingRow {
  'Campo Aliare': string;
  'Campo Customer Unit': string;
  'Campo Customer Group': string;
  Tipo: string;
  Descrição: string;
}

export const useExcelMappingUtils = () => {
  const exportToCSV = (
    mappings: AliareFieldMapping[],
    customerCustomFields: OrgSetupCustomField[],
    organizationName?: string,
  ) => {
    const headers = ['Campo Aliare', 'Campo Customer Unit', 'Campo Customer Group', 'Tipo', 'Descrição'];

    const csvData = mappings.map((mapping) => {
      const unitField = customerCustomFields.find((f) => f.identifier === mapping.customerUnitField);
      const groupField = customerCustomFields.find((f) => f.identifier === mapping.customerGroupField);

      return [
        mapping.aliareField,
        unitField?.label || mapping.customerUnitField || '',
        groupField?.label || mapping.customerGroupField || '',
        mapping.isFixedField ? 'Fixo' : 'Personalizável',
        mapping.description || '',
      ];
    });

    const csvContent = [
      [`Mapeamento de Campos - Aliare`],
      [`Organização: ${organizationName || 'N/A'}`],
      [`Data de Exportação: ${new Date().toLocaleDateString('pt-BR')}`],
      [''],
      ['Instruções:'],
      ['1. Preencha os campos "Campo Customer Unit" e "Campo Customer Group" com os identificadores'],
      ['2. Campos marcados como "Fixo" não podem ser alterados'],
      ['3. Salve o arquivo e importe novamente para atualizar os mapeamentos'],
      [''],
      headers,
      ...csvData,
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `mapeamento-aliare-${organizationName?.replace(/\s+/g, '-').toLowerCase() || 'org'}-${new Date().toISOString().split('T')[0]}.csv`;

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const importFromCSV = (file: File, customerCustomFields: OrgSetupCustomField[]): Promise<AliareFieldMapping[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const csvData = e.target?.result as string;
          const lines = csvData.split('\n');

          let dataStartIndex = -1;
          for (let i = 0; i < lines.length; i++) {
            if (lines[i].includes('Campo Aliare,Campo Customer Unit')) {
              dataStartIndex = i + 1;
              break;
            }
          }

          if (dataStartIndex === -1) {
            throw new Error('Formato de arquivo inválido. Não foi possível encontrar o cabeçalho dos dados.');
          }

          const mappings: AliareFieldMapping[] = [];

          for (let i = dataStartIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const columns = line.split(',');
            if (columns.length < 5) continue;

            const aliareField = columns[0];
            const unitFieldName = columns[1];
            const groupFieldName = columns[2];
            const tipo = columns[3];
            const descricao = columns[4];

            const unitField = customerCustomFields.find(
              (f) => f.label === unitFieldName || f.identifier === unitFieldName,
            );
            const groupField = customerCustomFields.find(
              (f) => f.label === groupFieldName || f.identifier === groupFieldName,
            );

            mappings.push({
              aliareField,
              customerUnitField: unitField?.identifier || unitFieldName || '',
              customerGroupField: groupField?.identifier || groupFieldName || '',
              isFixedField: tipo === 'Fixo',
              description: descricao,
            });
          }

          resolve(mappings);
        } catch (error) {
          reject(
            new Error(`Erro ao processar arquivo CSV: ${error instanceof Error ? error.message : 'Erro desconhecido'}`),
          );
        }
      };

      reader.onerror = () => {
        reject(new Error('Erro ao ler o arquivo'));
      };

      reader.readAsText(file);
    });
  };

  return {
    exportToExcel: exportToCSV,
    importFromExcel: importFromCSV,
  };
};
